import { useState, useCallback } from 'react';

interface UsePDFDownloadOptions {
  filename: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Generic hook for PDF download functionality
 * Handles loading state and download logic
 */
export function usePDFDownload<T = any>(
  generatePDF: () => Promise<Blob>,
  options: UsePDFDownloadOptions
) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleDownloadPDF = useCallback(async () => {
    try {
      setIsGeneratingPDF(true);

      const blob = await generatePDF();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = options.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);

      options.onSuccess?.();
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      const errorObj = error instanceof Error ? error : new Error(String(error));
      options.onError?.(errorObj);
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [generatePDF, options]);

  return {
    isGeneratingPDF,
    handleDownloadPDF,
  };
}
export function usePDFDownloadMulti<T = any>(
  generatePDF: (data: T[], ids: number[]) => Promise<any>,
  ids: number[],
  options: UsePDFDownloadOptions
) {
  const handleDownloadPDF = useCallback(async (data: T[]) => {
    try {
      const blob = await generatePDF(data, ids);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = options.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);

      options.onSuccess?.();
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      const errorObj = error instanceof Error ? error : new Error(String(error));
      options.onError?.(errorObj);
    } finally {
      options.onSuccess?.();
    }
  }, [generatePDF, options, ids]);

  return handleDownloadPDF;
}

